/**
 * Mock data for Central de Notas prototype.
 * Replace with tRPC queries when backend is ready.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NotaType = 'NF' | 'ND'
export type NotaStatus = 'pago' | 'aberto' | 'vencido' | 'cancelada'
export type DigitalPromo = 'digital' | 'promo'

export interface NotaFiscal {
  id: string
  tipo: NotaType
  numero: string
  cliente: string
  contrato: string | null
  tipoServico: string
  pit: string
  centroResultado: string
  digitalPromo: DigitalPromo
  servicoTipo: DigitalPromo // classificação do serviço (Digital ou Promo)
  valorBruto: number
  valorLiquido: number
  dataEmissao: string
  dataBaixa: string | null
  dataVencimento: string
  status: NotaStatus
  codigoCDA: string | null // only for ND
  destinatario: string // email do destinatário da nota
  favorecido: string   // pessoa/empresa favorecida na nota
  cnpj: string         // CNPJ do favorecido
  adiantamento?: {
    valor: number        // valor adiantado ao fornecedor para o job
    dataPagamento: string // ISO date — quando o adiantamento foi pago
  }
}

export interface ContaReceber {
  id: string
  cliente: string
  numeroNota: string
  valor: number
  dataEmissao: string
  dataVencimento: string
  dataBaixa: string | null
  diasAtraso: number
  status: NotaStatus
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function randomDate(start: string, end: string): string {
  const s = new Date(start).getTime()
  const e = new Date(end).getTime()
  const d = new Date(s + Math.random() * (e - s))
  return d.toISOString().split('T')[0]
}

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function randomAmount(min: number, max: number): number {
  return Math.round((min + Math.random() * (max - min)) * 100) / 100
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CLIENTES = [
  'Petrobras', 'Banco do Brasil', 'Vale', 'Itaú Unibanco', 'Bradesco',
  'Ambev', 'JBS', 'Magazine Luiza', 'Natura', 'Localiza',
  'Eletrobras', 'Sabesp', 'BNDES', 'Caixa Econômica', 'Embraer',
]

const CONTRATOS = ['0054', '15/19', '25/54', '0032', '0078', '0091']

// Mapa contrato → cliente (deve bater com contratos/mock-data.ts)
const CONTRATO_CLIENTE: Record<string, string> = {
  '0054': 'Petrobras',
  '15/19': 'Banco do Brasil',
  '25/54': 'Vale',
  '0032': 'Itaú Unibanco',
  '0078': 'Bradesco',
  '0091': 'Ambev',
}

// Limite contratual por contrato (fatia Monumenta = limite / agências)
// Usado para calibrar valores das notas e não estourar o contrato
const CONTRATO_FATIA_MONUMENTA: Record<string, number> = {
  '0054': 2_700_000,   // 5.4M / 2
  '15/19': 1_066_667,  // 3.2M / 3
  '25/54': 3_125_000,  // 6.25M / 2
  '0032': 2_800_000,   // 2.8M / 1
  '0078': 1_075_000,   // 2.15M / 2
  '0091': 2_066_667,   // 6.2M / 3
}

const TIPOS_SERVICO = [
  'Honorários', 'Hospedagem', 'Identidade Individual',
  'Projeto Executivo', 'Planejamento', 'Consultoria',
  'Design Digital', 'Produção Gráfica',
]

const PITS = [
  'DD-001', 'DD-002', 'DD-003', 'BS-001', 'BS-002',
  'BX-001', 'MS-001', 'MS-002',
]

const CENTROS_RESULTADO = [
  'CR-Digital', 'CR-Promo', 'CR-Eventos', 'CR-Consultoria',
]

const CODIGOS_CDA = [
  'CDA-101', 'CDA-202', 'CDA-303', 'CDA-404', 'CDA-505', null,
]

const FAVORECIDOS = [
  'Monumenta Comunicação Ltda', 'Monumenta Digital Ltda', 'Monumenta Promo Ltda',
  'Studio Criativo Ltda', 'Agência Norte Comunicação', 'MKT Solutions SA',
  'Pixel Produtora Ltda', 'Gráfica Express Ltda', 'EventoPro Serviços',
  'Consultoria Stratego Ltda', 'Digital House Tecnologia', 'Mídia Total SA',
]

const FAVORECIDO_CNPJ: Record<string, string> = {
  'Monumenta Comunicação Ltda': '12.345.678/0001-01',
  'Monumenta Digital Ltda': '12.345.678/0002-82',
  'Monumenta Promo Ltda': '12.345.678/0003-63',
  'Studio Criativo Ltda': '23.456.789/0001-10',
  'Agência Norte Comunicação': '34.567.890/0001-20',
  'MKT Solutions SA': '45.678.901/0001-30',
  'Pixel Produtora Ltda': '56.789.012/0001-40',
  'Gráfica Express Ltda': '67.890.123/0001-50',
  'EventoPro Serviços': '78.901.234/0001-60',
  'Consultoria Stratego Ltda': '89.012.345/0001-70',
  'Digital House Tecnologia': '90.123.456/0001-80',
  'Mídia Total SA': '01.234.567/0001-90',
}

// ---------------------------------------------------------------------------
// Generate Notas Fiscais + Notas de Débito
// ---------------------------------------------------------------------------

function generateNotas(count: number): NotaFiscal[] {
  const notas: NotaFiscal[] = []
  const today = new Date()

  for (let i = 0; i < count; i++) {
    const tipo: NotaType = Math.random() > 0.75 ? 'ND' : 'NF'
    const pit = randomItem(PITS)
    const digitalPromo: DigitalPromo = pit.startsWith('DD') ? 'digital' : 'promo'
    const dataEmissao = randomDate('2024-01-01', '2026-04-15')
    const emissaoDate = new Date(dataEmissao)
    const vencDate = new Date(emissaoDate)
    vencDate.setDate(vencDate.getDate() + randomItem([30, 45, 60, 90]))
    const dataVencimento = vencDate.toISOString().split('T')[0]

    let status: NotaStatus
    let dataBaixa: string | null = null

    const roll = Math.random()
    if (roll < 0.04) {
      // ~4% canceladas
      status = 'cancelada'
    } else if (roll < 0.65) {
      status = 'pago'
      const baixaDate = new Date(emissaoDate)
      baixaDate.setDate(baixaDate.getDate() + Math.floor(Math.random() * 60) + 5)
      dataBaixa = baixaDate.toISOString().split('T')[0]
    } else if (vencDate < today) {
      status = 'vencido'
    } else {
      status = 'aberto'
    }

    const contrato = tipo === 'ND'
      ? (Math.random() > 0.2 ? randomItem(CONTRATOS) : null) // 20% sem contrato (flag!)
      : randomItem(CONTRATOS)

    // Cliente vinculado ao contrato (quando existe); senão, aleatório
    const cliente = contrato && CONTRATO_CLIENTE[contrato]
      ? CONTRATO_CLIENTE[contrato]
      : randomItem(CLIENTES)

    // Valor proporcional ao limite contratual para não estourar
    // ~200 notas / 6 contratos ≈ 33 notas por contrato → cada nota ~2-3% do limite
    const fatia = contrato ? (CONTRATO_FATIA_MONUMENTA[contrato] ?? 1_500_000) : 1_500_000
    const maxNota = fatia * 0.04 // cada nota até ~4% do limite
    const valorBruto = randomAmount(5000, Math.min(maxNota, 200_000))
    const taxRate = 0.13 + Math.random() * 0.07
    const valorLiquido = Math.round(valorBruto * (1 - taxRate) * 100) / 100
    const emailDomain = cliente.toLowerCase().replace(/[^a-z]/g, '')
    const destinatario = `${randomItem(['financeiro', 'contas', 'faturamento', 'contabilidade', 'notas'])}@${emailDomain}.com.br`
    const tipoServico = randomItem(TIPOS_SERVICO)
    // Deriva classificação Digital/Promo do tipo de serviço (com alguma aleatoriedade para variar)
    const servicoTipo: DigitalPromo = /digital|hospedagem|consultoria/i.test(tipoServico)
      ? (Math.random() > 0.15 ? 'digital' : 'promo')
      : (Math.random() > 0.7 ? 'digital' : 'promo')
    const favorecido = randomItem(FAVORECIDOS)
    const cnpj = FAVORECIDO_CNPJ[favorecido] ?? '00.000.000/0000-00'

    notas.push({
      id: `nota-${i + 1}`,
      tipo,
      numero: `${tipo === 'NF' ? 'NF' : 'ND'}-${String(1000 + i).padStart(5, '0')}`,
      cliente,
      contrato,
      tipoServico,
      pit,
      centroResultado: randomItem(CENTROS_RESULTADO),
      digitalPromo,
      servicoTipo,
      valorBruto,
      valorLiquido,
      dataEmissao,
      dataBaixa,
      dataVencimento,
      status,
      codigoCDA: tipo === 'ND' ? randomItem(CODIGOS_CDA) : null,
      destinatario,
      favorecido,
      cnpj,
      // ~30% das notas têm adiantamento (valor entre 10-40% do bruto, pago 15-90 dias antes da emissão)
      adiantamento: Math.random() < 0.3
        ? (() => {
            const diasAntes = Math.floor(Math.random() * 76) + 15 // 15 a 90 dias antes
            const pgtoDate = new Date(emissaoDate)
            pgtoDate.setDate(pgtoDate.getDate() - diasAntes)
            const pctAdiantamento = 0.10 + Math.random() * 0.30 // 10% a 40%
            return {
              valor: Math.round(valorBruto * pctAdiantamento * 100) / 100,
              dataPagamento: pgtoDate.toISOString().split('T')[0],
            }
          })()
        : undefined,
    })
  }

  return notas.sort((a, b) => b.dataEmissao.localeCompare(a.dataEmissao))
}

// ---------------------------------------------------------------------------
// Generate Contas a Receber
// ---------------------------------------------------------------------------

function generateContasReceber(count: number): ContaReceber[] {
  const contas: ContaReceber[] = []
  const today = new Date()

  for (let i = 0; i < count; i++) {
    const dataEmissao = randomDate('2024-06-01', '2026-04-15')
    const emissaoDate = new Date(dataEmissao)
    const vencDate = new Date(emissaoDate)
    vencDate.setDate(vencDate.getDate() + randomItem([30, 45, 60, 90]))
    const dataVencimento = vencDate.toISOString().split('T')[0]

    let status: NotaStatus
    let dataBaixa: string | null = null
    let diasAtraso = 0

    if (Math.random() > 0.4) {
      status = 'pago'
      const baixaDate = new Date(emissaoDate)
      baixaDate.setDate(baixaDate.getDate() + Math.floor(Math.random() * 90) + 5)
      dataBaixa = baixaDate.toISOString().split('T')[0]
    } else if (vencDate < today) {
      status = 'vencido'
      diasAtraso = Math.floor((today.getTime() - vencDate.getTime()) / (1000 * 60 * 60 * 24))
    } else {
      status = 'aberto'
    }

    contas.push({
      id: `cr-${i + 1}`,
      cliente: randomItem(CLIENTES),
      numeroNota: `NF-${String(2000 + i).padStart(5, '0')}`,
      valor: randomAmount(8000, 1200000),
      dataEmissao,
      dataVencimento,
      dataBaixa,
      diasAtraso,
      status,
    })
  }

  return contas.sort((a, b) => b.dataEmissao.localeCompare(a.dataEmissao))
}

// ---------------------------------------------------------------------------
// Contas a Pagar types
// ---------------------------------------------------------------------------

export interface ContaPagar {
  id: string
  fornecedor: string
  categoria: string
  numeroDocumento: string
  valor: number
  dataEmissao: string
  dataVencimento: string
  dataPagamento: string | null
  diasAtraso: number
  status: NotaStatus
}

// ---------------------------------------------------------------------------
// Generate Contas a Pagar
// ---------------------------------------------------------------------------

const FORNECEDORES = [
  'Studio Alpha', 'Gráfica Express', 'Produtora Vídeo Plus', 'Fotógrafo João Silva',
  'Agência Digital Z', 'Freelancer Maria Costa', 'Impressão Total', 'Logística Rápida',
  'Cenografia Criativa', 'Som & Luz Eventos', 'Catering Premium', 'Tradução Global',
  'Dev Outsource', 'Cloud AWS', 'Adobe Creative', 'Aluguel Escritório',
]

const CATEGORIAS_FORNECEDOR = [
  'Terceiros', 'Freelancers', 'Fornecedores', 'Infraestrutura',
  'Software', 'Aluguel', 'Eventos', 'Produção',
]

function generateContasPagar(count: number): ContaPagar[] {
  const contas: ContaPagar[] = []
  const today = new Date()

  for (let i = 0; i < count; i++) {
    const dataEmissao = randomDate('2024-06-01', '2026-04-15')
    const emissaoDate = new Date(dataEmissao)
    const vencDate = new Date(emissaoDate)
    vencDate.setDate(vencDate.getDate() + randomItem([15, 30, 45, 60]))
    const dataVencimento = vencDate.toISOString().split('T')[0]

    let status: NotaStatus
    let dataPagamento: string | null = null
    let diasAtraso = 0

    if (Math.random() > 0.45) {
      status = 'pago'
      const pagDate = new Date(emissaoDate)
      pagDate.setDate(pagDate.getDate() + Math.floor(Math.random() * 70) + 5)
      dataPagamento = pagDate.toISOString().split('T')[0]
    } else if (vencDate < today) {
      status = 'vencido'
      diasAtraso = Math.floor((today.getTime() - vencDate.getTime()) / (1000 * 60 * 60 * 24))
    } else {
      status = 'aberto'
    }

    contas.push({
      id: `cp-${i + 1}`,
      fornecedor: randomItem(FORNECEDORES),
      categoria: randomItem(CATEGORIAS_FORNECEDOR),
      numeroDocumento: `DOC-${String(5000 + i).padStart(5, '0')}`,
      valor: randomAmount(1500, 350000),
      dataEmissao,
      dataVencimento,
      dataPagamento,
      diasAtraso,
      status,
    })
  }

  return contas.sort((a, b) => b.dataEmissao.localeCompare(a.dataEmissao))
}

// ---------------------------------------------------------------------------
// Exported mock datasets (generated once)
// ---------------------------------------------------------------------------

// Ensure at least 3 notes have "Vencido" status for UI visibility
function ensureOverdueSamples(notas: NotaFiscal[]): NotaFiscal[] {
  const today = new Date()
  const overdueCount = notas.filter((n) => n.status === 'vencido').length
  if (overdueCount >= 3) return notas

  const needed = 3 - overdueCount
  const candidates = notas.filter((n) => n.status !== 'vencido')
  for (let i = 0; i < needed && i < candidates.length; i++) {
    const n = candidates[i]
    const vencDate = new Date(today)
    vencDate.setDate(vencDate.getDate() - (15 + i * 20))
    n.status = 'vencido'
    n.dataBaixa = null
    n.dataVencimento = vencDate.toISOString().split('T')[0]
  }
  return notas
}

export const MOCK_NOTAS: NotaFiscal[] = ensureOverdueSamples(generateNotas(200))
export const MOCK_CONTAS_RECEBER: ContaReceber[] = generateContasReceber(150)
export const MOCK_CONTAS_PAGAR: ContaPagar[] = generateContasPagar(120)
